import { useId, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { PERMISSION_SLUGS } from '../../../../utils/auth/permissionConstants';
import { addExpenseTag } from '../../../../services/TransactionService';
import './ExpenseTagsPicker.css';

const ExpenseTagsPicker = ({ tags, selectedNames, onChange }) => {
  const authUser = useSelector((state) => state.auth.authUser);
  const userPermissionSlugs = authUser?.all_permission_slugs || [];
  const canManageExpenseTags = userPermissionSlugs.includes(PERMISSION_SLUGS.VIEW_PARTNER_DETAILS);
  const partnerId = authUser?.partner_id;
  const queryClient = useQueryClient();

  const [showAddForm, setShowAddForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [addError, setAddError] = useState(null);

  const addTagMutation = useMutation({
    mutationFn: (name) => addExpenseTag(partnerId, { name }),
    onSuccess: (response) => {
      const createdName = response?.data?.data?.name ?? newTagName.trim();
      queryClient.invalidateQueries({ queryKey: ['allExpenseTags', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['expenseTags', partnerId] });
      if (createdName && !selectedNames.includes(createdName)) {
        onChange([...selectedNames, createdName]);
      }
      setNewTagName('');
      setAddError(null);
      setShowAddForm(false);
      toast.success('Expense tag added.');
    },
    onError: (err) => {
      const message = err?.response?.data?.errors?.name?.[0]
        || err?.response?.data?.error
        || err?.response?.data?.message
        || 'Could not add the expense tag. Please try again.';
      setAddError(message);
    },
  });

  const submitNewTag = () => {
    const trimmed = newTagName.trim();
    if (!trimmed) {
      setAddError('Enter a tag name.');
      return;
    }
    addTagMutation.mutate(trimmed);
  };

  // Enter and Escape inside the tag name input must NOT bubble up to the
  // parent spend form. The picker is nested inside SendMoneyForm,
  // PaybillForm, BuyGoodsForm, and PochiLaBiasharaForm, each of which is
  // a real <form onSubmit>. A bubbling Enter would trigger the parent
  // submit and could fire an actual STK push while the user is typing a
  // tag name, so both keys are handled locally and their propagation is
  // stopped explicitly. Escape closes the inline add. Enter submits it.
  const handleAddInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      submitNewTag();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setShowAddForm(false);
      setNewTagName('');
      setAddError(null);
    }
  };
  const [searchQuery, setSearchQuery] = useState('');
  const componentId = useId();

  const availableTagNames = useMemo(
    () => (tags || []).filter(tag => tag.name).map(tag => tag.name),
    [tags]
  );

  const filteredTagNames = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return availableTagNames;
    return availableTagNames.filter(name => name.toLowerCase().includes(query));
  }, [availableTagNames, searchQuery]);

  const toggleTag = (tagName) => {
    if (selectedNames.includes(tagName)) {
      onChange(selectedNames.filter(name => name !== tagName));
    } else {
      onChange([...selectedNames, tagName]);
    }
  };

  return (
    <div className="expense-tags-picker">
      <label className="form-label text-muted small d-block mb-2">
        Expense Tag (select many, {selectedNames.length} selected):
      </label>
      {availableTagNames.length > 0 && (
        <input
          type="search"
          className="form-control form-control-sm expense-tags-picker__search"
          placeholder="Search tags"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      )}
      <div className="expense-tags-picker__list">
        {availableTagNames.length === 0 ? (
          <div className="text-muted small">
            No expense tags available.
            {! canManageExpenseTags && (
              <>{' '}Ask a super admin to add one.</>
            )}
          </div>
        ) : filteredTagNames.length === 0 ? (
          <div className="text-muted small">No tags match your search.</div>
        ) : (
          filteredTagNames.map((tagName) => {
            const inputId = `${componentId}-${tagName}`;
            return (
              <div className="form-check" key={tagName}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={inputId}
                  checked={selectedNames.includes(tagName)}
                  onChange={() => toggleTag(tagName)}
                />
                <label className="form-check-label" htmlFor={inputId}>
                  {tagName}
                </label>
              </div>
            );
          })
        )}
      </div>
      {canManageExpenseTags && (
        <div className="mt-2">
          {showAddForm ? (
            <div className="d-flex gap-2 align-items-start">
              <div className="flex-grow-1">
                <input
                  type="text"
                  className="form-control form-control-sm expense-tags-picker__add-input"
                  placeholder="New tag name"
                  value={newTagName}
                  onChange={(e) => { setNewTagName(e.target.value); setAddError(null); }}
                  onKeyDown={handleAddInputKeyDown}
                  autoFocus
                  maxLength={60}
                  disabled={addTagMutation.isPending}
                />
                {addError && <small className="text-danger d-block mt-1">{addError}</small>}
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={submitNewTag}
                disabled={addTagMutation.isPending}
              >
                {addTagMutation.isPending ? 'Adding...' : 'Add'}
              </button>
              <button
                type="button"
                className="btn btn-link btn-sm text-muted"
                onClick={() => { setShowAddForm(false); setNewTagName(''); setAddError(null); }}
                disabled={addTagMutation.isPending}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={() => setShowAddForm(true)}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add expense tag
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpenseTagsPicker;
